import { useState, useEffect } from 'react'

/**
 * DeviceApproveChallenge
 *
 * Renders on the authenticated phone after it scans the QR.
 * Shows device info and the gamified re-auth challenge.
 *
 * Challenge types:
 *   biometric   — WebAuthn Face ID / Touch ID (most common)
 *   symbol      — 3×3 constellation symbol picker
 *   wallet_code — 6-digit TOTP entry
 *   pattern     — 4-point constellation glyph tap
 */

export type ChallengeType = 'biometric' | 'symbol' | 'wallet_code' | 'pattern'

interface Props {
  sessionId:     string
  challengeType: ChallengeType
  deviceHint:    string    // e.g. "Chrome · macOS"
  requestedAt:   string    // ISO string
  onApproved:    () => void
  onDenied:      () => void
}

// ─── Constellation symbols ───────────────────────────────────────────────────

const STARS = [
  { id: 'identity', glyph: 'Λ',  label: 'Identity' },
  { id: 'memory',   glyph: '✦',  label: 'Memory'   },
  { id: 'vision',   glyph: '🔬', label: 'Vision'   },
  { id: 'bio',      glyph: '🌱', label: 'Bio'       },
  { id: 'dream',    glyph: '🌙', label: 'Dream'     },
  { id: 'ethics',   glyph: '⚖', label: 'Ethics'   },
  { id: 'guardian', glyph: '🛡', label: 'Guardian' },
  { id: 'quantum',  glyph: '⚛', label: 'Quantum'  },
  { id: 'matriz',   glyph: '◈',  label: 'MATRIZ'   },
]

// Shuffle for display so position isn't predictable
function shuffledStars() {
  return [...STARS].sort(() => Math.random() - 0.5)
}

// ─── Challenge renderers ──────────────────────────────────────────────────────

function BiometricChallenge({ onComplete }: { onComplete: (resp: string) => void }) {
  const [status, setStatus] = useState<'idle' | 'pending' | 'done' | 'error'>('idle')

  const trigger = async () => {
    if (!window.PublicKeyCredential) {
      // Fallback: treat as direct approval on this trusted device
      onComplete('direct_approval')
      return
    }
    setStatus('pending')
    try {
      // Use a simple get() call to trigger platform authenticator
      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge:        crypto.getRandomValues(new Uint8Array(32)),
          rpId:             window.location.hostname,
          userVerification: 'required',
          timeout:          60_000,
        },
      })
      if (assertion) {
        setStatus('done')
        onComplete(JSON.stringify({ type: 'webauthn', id: (assertion as any).id }))
      }
    } catch (err: any) {
      if (err?.name === 'NotAllowedError') setStatus('idle')
      else setStatus('error')
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
      <button
        onClick={trigger}
        disabled={status === 'pending' || status === 'done'}
        style={{
          width:         '80px',
          height:        '80px',
          borderRadius:  '50%',
          background:    status === 'done' ? 'rgba(96,165,250,0.15)' : 'rgba(255,255,255,0.04)',
          border:        `1px solid ${status === 'done' ? 'rgba(96,165,250,0.5)' : 'rgba(255,255,255,0.1)'}`,
          display:       'flex',
          alignItems:    'center',
          justifyContent: 'center',
          cursor:        'pointer',
          transition:    'all 0.2s',
        }}
        aria-label="Verify with biometrics"
      >
        {status === 'pending' ? (
          <div style={{
            width: '24px', height: '24px',
            border: '1px solid rgba(96,165,250,0.3)',
            borderTopColor: 'rgba(96,165,250,0.8)',
            borderRadius: '50%', animation: 'challenge-spin 0.8s linear infinite',
          }}/>
        ) : status === 'done' ? (
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path d="M8 16l6 6 10-12" stroke="#60A5FA" strokeWidth="1.5"
                  strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
               stroke="rgba(255,255,255,0.5)" strokeWidth="1" strokeLinecap="round">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
            <path d="M12 8v4M12 16h.01"/>
          </svg>
        )}
      </button>
      <p style={{
        fontFamily:    'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize:      '9px',
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        color:         'rgba(255,255,255,0.35)',
        margin:        0,
        textAlign:     'center',
      }}>
        {status === 'idle'    ? 'Tap to verify with Face ID / Touch ID'  : ''}
        {status === 'pending' ? 'Waiting for biometric…'                  : ''}
        {status === 'done'    ? 'Verified'                                 : ''}
        {status === 'error'   ? 'Verification failed — tap to retry'       : ''}
      </p>
    </div>
  )
}

function SymbolChallenge({ onComplete }: { onComplete: (resp: string) => void }) {
  const [stars]    = useState(() => shuffledStars())
  const [selected, setSelected] = useState<string | null>(null)

  const handleSelect = (id: string) => {
    setSelected(id)
    // Small delay for visual feedback
    setTimeout(() => onComplete(id), 300)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
      <p style={{
        fontFamily:    'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize:      '8px',
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
        color:         'rgba(255,255,255,0.3)',
        margin:        0,
      }}>
        Select your constellation symbol
      </p>

      <div style={{
        display:             'grid',
        gridTemplateColumns: 'repeat(3, 64px)',
        gap:                 '8px',
      }}>
        {stars.map(star => (
          <button
            key={star.id}
            onClick={() => handleSelect(star.id)}
            title={star.label}
            style={{
              width:         '64px',
              height:        '64px',
              display:       'flex',
              alignItems:    'center',
              justifyContent: 'center',
              fontSize:      '24px',
              background:    selected === star.id ? 'rgba(96,165,250,0.15)' : 'rgba(255,255,255,0.03)',
              border:        `1px solid ${selected === star.id ? 'rgba(96,165,250,0.4)' : 'rgba(255,255,255,0.08)'}`,
              borderRadius:  '10px',
              cursor:        'pointer',
              transition:    'all 0.15s',
            }}
            onMouseEnter={e => {
              if (selected !== star.id)
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'
            }}
            onMouseLeave={e => {
              if (selected !== star.id)
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'
            }}
            aria-label={star.label}
          >
            {star.glyph}
          </button>
        ))}
      </div>
    </div>
  )
}

function WalletCodeChallenge({ onComplete }: { onComplete: (resp: string) => void }) {
  const [code, setCode] = useState('')

  const handleInput = (v: string) => {
    const digits = v.replace(/\D/g, '').slice(0, 6)
    setCode(digits)
    if (digits.length === 6) onComplete(digits)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
      <p style={{
        fontFamily:    'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize:      '8px',
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
        color:         'rgba(255,255,255,0.3)',
        margin:        0,
      }}>
        Enter code from your LUKHΛS pass
      </p>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={6}
        value={code}
        onChange={e => handleInput(e.target.value)}
        placeholder="000000"
        autoFocus
        style={{
          width:         '160px',
          textAlign:     'center',
          fontFamily:    'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize:      '28px',
          fontWeight:    300,
          letterSpacing: '0.3em',
          color:         '#fff',
          background:    'rgba(255,255,255,0.04)',
          border:        '1px solid rgba(255,255,255,0.12)',
          borderRadius:  '8px',
          padding:       '12px 8px',
          outline:       'none',
        }}
        onFocus={e  => (e.target.style.borderColor = 'rgba(96,165,250,0.4)')}
        onBlur={e   => (e.target.style.borderColor = 'rgba(255,255,255,0.12)')}
      />
      <p style={{
        fontFamily:    'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize:      '8px',
        letterSpacing: '0.2em',
        color:         'rgba(100,116,139,0.4)',
        margin:        0,
      }}>
        Rotates every 60 seconds
      </p>
    </div>
  )
}

function PatternChallenge({ onComplete }: { onComplete: (resp: string) => void }) {
  const [sequence, setSequence] = useState<string[]>([])
  const PATTERN_STARS = STARS.slice(0, 9)

  const tap = (id: string) => {
    const next = [...sequence, id]
    setSequence(next)
    if (next.length === 4) {
      onComplete(next.join(','))
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
      <p style={{
        fontFamily:    'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize:      '8px',
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
        color:         'rgba(255,255,255,0.3)',
        margin:        0,
      }}>
        Tap your 4-symbol sequence
      </p>

      {/* Tap dots indicator */}
      <div style={{ display: 'flex', gap: '8px' }}>
        {[0,1,2,3].map(i => (
          <div key={i} style={{
            width:        '8px',
            height:       '8px',
            borderRadius: '50%',
            background:   i < sequence.length ? 'rgba(96,165,250,0.8)' : 'rgba(255,255,255,0.1)',
            transition:   'background 0.15s',
          }} />
        ))}
      </div>

      <div style={{
        display:             'grid',
        gridTemplateColumns: 'repeat(3, 60px)',
        gap:                 '8px',
      }}>
        {PATTERN_STARS.map(star => {
          const tapped = sequence.includes(star.id)
          return (
            <button
              key={star.id}
              onClick={() => !tapped && tap(star.id)}
              disabled={tapped || sequence.length >= 4}
              title={star.label}
              style={{
                width:         '60px',
                height:        '60px',
                display:       'flex',
                alignItems:    'center',
                justifyContent: 'center',
                fontSize:      '22px',
                background:    tapped ? 'rgba(96,165,250,0.12)' : 'rgba(255,255,255,0.03)',
                border:        `1px solid ${tapped ? 'rgba(96,165,250,0.35)' : 'rgba(255,255,255,0.07)'}`,
                borderRadius:  '10px',
                cursor:        tapped || sequence.length >= 4 ? 'default' : 'pointer',
                opacity:       tapped ? 0.5 : 1,
                transition:    'all 0.15s',
              }}
              aria-label={star.label}
            >
              {star.glyph}
            </button>
          )
        })}
      </div>

      {sequence.length > 0 && sequence.length < 4 && (
        <button
          onClick={() => setSequence([])}
          style={{
            fontFamily:    'ui-monospace', fontSize: '8px', letterSpacing: '0.2em',
            textTransform: 'uppercase', color: 'rgba(100,116,139,0.4)',
            background:    'none', border: 'none', cursor: 'pointer', padding: 0,
          }}
        >
          Reset
        </button>
      )}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function DeviceApproveChallenge({
  sessionId, challengeType, deviceHint, requestedAt, onApproved, onDenied,
}: Props) {
  const [submitting, setSubmitting] = useState(false)
  const [error,      setError]      = useState<string | null>(null)
  const [result,     setResult]     = useState<'approved' | 'denied' | null>(null)

  const submit = async (challengeResponse: string, action: 'approve' | 'deny' = 'approve') => {
    setSubmitting(true)
    setError(null)
    try {
      const res = await fetch('/api/auth/device/approve', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ sessionId, action, challengeType, challengeResponse }),
      })
      const data = await res.json()

      if (data.ok && data.result === 'approved') {
        setResult('approved')
        onApproved()
      } else if (data.ok && data.result === 'denied') {
        setResult('denied')
        onDenied()
      } else {
        setError(data.error ?? 'Verification failed')
        if (data.locked) {
          setResult('denied')
          onDenied()
        }
      }
    } catch {
      setError('Connection error')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeny = () => submit('', 'deny')

  const CHALLENGE_TITLES: Record<ChallengeType, string> = {
    biometric:   'Verify with biometrics',
    symbol:      'Choose your symbol',
    wallet_code: 'Enter pass code',
    pattern:     'Tap your sequence',
  }

  return (
    <div style={{
      minHeight:     '100vh',
      background:    '#0A0F1A',
      display:       'flex',
      alignItems:    'center',
      justifyContent: 'center',
      padding:       '24px',
    }}>
      <div style={{
        width:         '100%',
        maxWidth:      '360px',
        display:       'flex',
        flexDirection: 'column',
        gap:           '28px',
      }}>
        {/* Accession header */}
        <div style={{ textAlign: 'center' }}>
          <p style={{
            fontFamily:    'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize:      '8px',
            letterSpacing: '0.35em',
            textTransform: 'uppercase',
            color:         'rgba(96,165,250,0.4)',
            margin:        '0 0 4px',
          }}>
            LKH-IDENTITY-04
          </p>
          <h2 style={{
            fontFamily:  '"Helvetica Neue", Helvetica, Arial, sans-serif',
            fontSize:    '22px',
            fontWeight:  100,
            color:       'rgba(255,255,255,0.85)',
            margin:      0,
            letterSpacing: '0.02em',
          }}>
            {CHALLENGE_TITLES[challengeType]}
          </h2>
        </div>

        {/* Device info */}
        <div style={{
          padding:      '12px 16px',
          background:   'rgba(255,255,255,0.03)',
          border:       '1px solid rgba(255,255,255,0.07)',
          borderRadius: '8px',
        }}>
          <p style={{
            fontFamily:    'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize:      '8px',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color:         'rgba(255,255,255,0.25)',
            margin:        '0 0 4px',
          }}>
            Sign-in request from
          </p>
          <p style={{
            fontFamily:  '"Helvetica Neue", Helvetica, Arial, sans-serif',
            fontSize:    '13px',
            fontWeight:  300,
            color:       'rgba(255,255,255,0.7)',
            margin:      0,
          }}>
            {deviceHint}
          </p>
          <p style={{
            fontFamily:    'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize:      '9px',
            color:         'rgba(100,116,139,0.5)',
            margin:        '4px 0 0',
            letterSpacing: '0.1em',
          }}>
            {new Date(requestedAt).toLocaleTimeString()} UTC
          </p>
        </div>

        {/* Challenge */}
        {result === null && !submitting && (
          <>
            {challengeType === 'biometric'   && <BiometricChallenge   onComplete={r => submit(r)} />}
            {challengeType === 'symbol'      && <SymbolChallenge      onComplete={r => submit(r)} />}
            {challengeType === 'wallet_code' && <WalletCodeChallenge  onComplete={r => submit(r)} />}
            {challengeType === 'pattern'     && <PatternChallenge     onComplete={r => submit(r)} />}
          </>
        )}

        {submitting && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{
              width: '18px', height: '18px',
              border: '1px solid rgba(96,165,250,0.2)',
              borderTopColor: 'rgba(96,165,250,0.7)',
              borderRadius: '50%', animation: 'challenge-spin 0.8s linear infinite',
            }}/>
          </div>
        )}

        {/* Error */}
        {error && (
          <p style={{
            fontFamily:    'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize:      '9px',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color:         'rgba(239,68,68,0.6)',
            textAlign:     'center',
            margin:        0,
          }}>
            {error}
          </p>
        )}

        {/* Result */}
        {result === 'approved' && (
          <p style={{ fontFamily: 'ui-monospace', fontSize: '9px', letterSpacing: '0.2em',
            textTransform: 'uppercase', color: 'rgba(96,165,250,0.6)', textAlign: 'center', margin: 0 }}>
            Approved — the other device is now signed in
          </p>
        )}

        {/* Deny button */}
        {result === null && (
          <button onClick={handleDeny} disabled={submitting} style={{
            fontFamily:    'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize:      '8px',
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
            color:         'rgba(239,68,68,0.45)',
            background:    'none',
            border:        'none',
            cursor:        'pointer',
            padding:       '4px 0',
            textAlign:     'center',
          }}>
            Deny this request
          </button>
        )}
      </div>

      <style>{`@keyframes challenge-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
